import React, { useState, useEffect } from "react";
import {
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Box,
} from "@mui/material";
import type { Permission } from "@/types/auth";

/**
 * Formulario para crear/editar permisos
 *
 * REGLAS APLICADAS:
 * - Solo <Stack> para layout (NO Grid)
 * - Componentes con size="small"
 * - Validación completa
 * - Máximo 500 líneas
 */

interface PermissionFormProps {
  open: boolean;
  permission?: Permission | null;
  onClose: () => void;
  onSave: (
    permission: Omit<
      Permission,
      "id" | "fecha_creacion" | "fecha_actualizacion"
    >,
  ) => Promise<void>;
}

export function PermissionForm({
  open,
  permission,
  onClose,
  onSave,
}: PermissionFormProps) {
  const [formData, setFormData] = useState<
    Omit<Permission, "id" | "fecha_creacion" | "fecha_actualizacion">
  >({
    nombre: "",
    descripcion: "",
    activo: true,
    es_sistema: false,
    estructura_permisos: {},
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Cargar datos del permiso a editar
  useEffect(() => {
    if (permission) {
      setFormData({
        nombre: permission.nombre,
        descripcion: permission.descripcion || "",
        activo: permission.activo,
        es_sistema: permission.es_sistema,
        estructura_permisos: permission.estructura_permisos || {},
      });
    } else {
      setFormData({
        nombre: "",
        descripcion: "",
        activo: true,
        es_sistema: false,
        estructura_permisos: {},
      });
    }
    setError(null);
    setFieldErrors({});
  }, [permission, open]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Validar nombre
    if (!formData.nombre.trim()) {
      errors["nombre"] = "El nombre es obligatorio";
    } else if (formData.nombre.length < 2) {
      errors["nombre"] = "El nombre debe tener al menos 2 caracteres";
    } else if (formData.nombre.length > 50) {
      errors["nombre"] = "El nombre no puede exceder 50 caracteres";
    } else if (!/^[a-zA-Z0-9_-]+$/.test(formData.nombre)) {
      errors["nombre"] =
        "El nombre solo puede contener letras, números, guiones y guiones bajos";
    }

    // Validar descripción (opcional)
    if (formData.descripcion && formData.descripcion.trim()) {
      if (formData.descripcion.length < 5) {
        errors["descripcion"] =
          "La descripción debe tener al menos 5 caracteres";
      } else if (formData.descripcion.length > 255) {
        errors["descripcion"] =
          "La descripción no puede exceder 255 caracteres";
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (
    field: keyof typeof formData,
    value: string | boolean | Record<string, any>,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Limpiar error del campo específico
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await onSave(formData);
      onClose();
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Error desconocido al guardar el permiso");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const isEditing = Boolean(permission?.id);
  const isSystemPermission = permission?.nombre === "all";

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6">
          {isEditing
            ? `Editar Permiso: ${permission?.nombre}`
            : "Crear Nuevo Permiso"}
        </Typography>
        {isSystemPermission && (
          <Typography variant="body2" color="warning.main">
            Este es un permiso del sistema y tiene limitaciones de edición
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Nombre del permiso */}
          <TextField
            label="Nombre del Permiso"
            value={formData.nombre}
            onChange={(e) => handleInputChange("nombre", e.target.value)}
            error={Boolean(fieldErrors["nombre"])}
            helperText={
              fieldErrors["nombre"] ||
              "Nombre único para identificar el permiso"
            }
            size="small"
            fullWidth
            disabled={loading || isSystemPermission}
            placeholder="ej: administrador, editor, visualizador"
          />

          {/* Descripción */}
          <TextField
            label="Descripción"
            value={formData.descripcion}
            onChange={(e) => handleInputChange("descripcion", e.target.value)}
            error={Boolean(fieldErrors["descripcion"])}
            helperText={
              fieldErrors["descripcion"] ||
              "Descripción detallada del propósito del permiso"
            }
            size="small"
            fullWidth
            multiline
            rows={3}
            disabled={loading}
            placeholder="Describe las funcionalidades que otorga este permiso..."
          />

          {/* Estado activo */}
          <FormControlLabel
            control={
              <Switch
                checked={formData.activo}
                onChange={(e) => handleInputChange("activo", e.target.checked)}
                size="small"
                disabled={loading || isSystemPermission}
              />
            }
            label="Permiso activo"
          />

          {/* Información sobre estructura de permisos */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Estructura de Permisos
            </Typography>
            <Typography variant="body2" color="text.secondary">
              La estructura detallada de permisos se puede configurar usando el
              editor JSON especializado después de crear el permiso básico.
            </Typography>
            {Object.keys(formData.estructura_permisos).length > 0 && (
              <Typography variant="body2" color="primary">
                ✓ Este permiso tiene{" "}
                {Object.keys(formData.estructura_permisos).length} ruta(s)
                configurada(s)
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading} size="small">
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          size="small"
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          {loading ? "Guardando..." : isEditing ? "Actualizar" : "Crear"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
